import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Empty, Loader, PageHeader, btn } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";
import { TriumvirateReplayModal } from "@/components/triumvirate/TriumvirateReplayModal";
import {
  TriumvirateHeroPanel,
  TriumvirateSectionNav,
  deltaText,
  formatDate,
} from "@/components/triumvirate/TriumvirateSectionChrome";

const PAGE_SIZE = 10;

export default function TriumvirateMatches() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openingMatchId, setOpeningMatchId] = useState("");
  const [replay, setReplay] = useState(null);

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const [matchesRes, profileRes] = await Promise.all([
        api.get(`/triumvirate/matches?page=${targetPage}&limit=${PAGE_SIZE}`),
        api.get("/triumvirate/profile"),
      ]);
      setData(matchesRes.data);
      setProfile(profileRes.data);
      setPage(matchesRes.data.page);
    } catch (error) {
      toast.error(apiErr(error, "Could not load Ludus Trias matches"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const openReplay = async (matchId) => {
    setOpeningMatchId(matchId);
    try {
      const { data: result } = await api.get(`/triumvirate/matches/${matchId}`);
      setReplay(result.match);
    } catch (error) {
      toast.error(apiErr(error, "Could not load the replay"));
    } finally {
      setOpeningMatchId("");
    }
  };

  if (loading && !data) return <Loader label="Loading Ludus Trias matches…" />;

  const matches = data?.items || [];
  const totalPages = data?.total_pages || 1;

  return (
    <div data-testid="triumvirate-matches-page" className="space-y-6">
      <PageHeader title="Ludus Trias Matches" subtitle="Review every ranked 3v3 result and open the full battle record." />
      <TriumvirateHeroPanel
        pageKey="triumvirate-matches"
        title="Follow the record of your Ludus Trias campaign."
        copy="Each page holds ten completed matches, newest first. Open any result for its round-by-round battle record."
        ratings={profile?.ratings}
        careerRatings={profile?.career_ratings}
        season={profile?.season}
      />
      <TriumvirateSectionNav />

      <section data-testid="triumvirate-matches-list-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5 sm:p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-cinzel text-2xl text-amber-200">Match History</h3>
            <p data-testid="triumvirate-matches-count" className="mt-1 text-sm text-zinc-500">{data?.total || 0} completed match{data?.total === 1 ? "" : "es"}</p>
          </div>
          <div data-testid="triumvirate-matches-page-status" className="text-xs uppercase tracking-[0.16em] text-zinc-500">Page {page} of {totalPages}</div>
        </div>

        <div className="mt-5 space-y-3" data-testid="triumvirate-matches-list">
          {matches.length === 0 ? <Empty label="No Ludus Trias matches recorded yet." /> : matches.map((match) => {
            const won = match.winner_user_id === user?.user_id;
            return (
              <button key={match.match_id} type="button" data-testid={`triumvirate-match-row-${match.match_id}`} onClick={() => openReplay(match.match_id)} className="w-full text-left border border-zinc-800 bg-zinc-950/50 p-4 rounded-sm hover:border-amber-700/60 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className={`font-cinzel text-lg ${won ? "text-emerald-300" : "text-red-300"}`}>{won ? "Victory" : "Defeat"}</div>
                    <p className="mt-1 text-sm text-zinc-300">{match.attacker_name} vs {match.defender_name}</p>
                    <p className="mt-1 text-xs text-zinc-500">{match.rounds} rounds · {formatDate(match.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <div data-testid={`triumvirate-match-delta-${match.match_id}`} className={`font-semibold ${won ? "text-emerald-300" : "text-red-300"}`}>{openingMatchId === match.match_id ? "Loading replay…" : deltaText(match.my_rating_delta || 0)}</div>
                    <div className="text-[11px] text-zinc-500">rating change</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button data-testid="triumvirate-matches-previous-page" type="button" disabled={loading || page <= 1} onClick={() => load(page - 1)} className={btn("ghost")}>Previous</button>
          <button data-testid="triumvirate-matches-next-page" type="button" disabled={loading || page >= totalPages} onClick={() => load(page + 1)} className={btn("ghost")}>Next</button>
        </div>
      </section>
      <TriumvirateReplayModal match={replay} open={!!replay} onClose={() => setReplay(null)} userId={user?.user_id} />
    </div>
  );
}